import React from 'react';

import type { useCustomer } from '@order/Customer';
import type { Address } from '@order/graphql';
import type { useCart } from '@order/hooks';

import {
  useConflictReview,
  useReorderState,
} from '../../../ReorderingScreen/hooks';

// Extracts order address and reorders it if needed.
export const useOrderAddress = ({ cart, customer }: UseOrderAddressProps) => {
  // Reorder Flow - trigger when cart address exists with a different id.
  const { reordering } = useReorderState();
  const conflictReview = useConflictReview();

  // Checkout address, might need reordering and might not be in customer yet.
  const { address, needsReorder, isInCustomer } = checkIfNeedsDeliveryReorder({
    cart,
    customer,
  });

  React.useEffect(() => {
    if (!needsReorder || reordering) return;

    void conflictReview({
      remainOnCheckout: true,
      orderId: cart?.id,
      orderType: 'delivery',
      restaurantId: cart?.restaurant?.id ?? '',
      restaurantSlug: cart?.restaurant?.slug ?? '',
      restaurantName: cart?.restaurant?.name ?? '',
      deliveryAddressName: address?.name ?? '',
      deliveryOrderDetail: {
        addressId: address?.id ?? '',
        deliveryFee: cart?.deliveryOrderDetail?.deliveryFee ?? 0,
        tip: cart?.deliveryOrderDetail?.tip ?? 0,
        vendor: cart?.deliveryOrderDetail?.vendor ?? '',
        vendorRestaurantId: cart?.deliveryOrderDetail?.vendorRestaurantId ?? '',
      },
    });
  }, [cart, needsReorder, reordering, address, conflictReview]);

  return React.useMemo(
    () => ({
      orderAddress: address,
      addressId: address?.id,
      hasAddress: isInCustomer, // if it's not in customer, show address form.
      isAddressReady: !reordering, // if it's reordering, it will change.
    }),
    [address, isInCustomer, reordering],
  );
};

// Delivery checkout needs reorder if cart address exists with another id.
// This can happen if the customer started a delivery order while unauthenticated
// or if they managed to delete their address without clearing the cart.
const checkIfNeedsDeliveryReorder = ({
  cart,
  customer,
}: UseOrderAddressProps) => {
  // Cart Address - needs to point to an existing customer address.
  const cartDeliveryDetails = cart?.deliveryOrderDetail;
  const cartAddress: Partial<Address> = cartDeliveryDetails?.address ?? {};
  const cartAddressId = cartAddress?.id ?? '';

  // No Cart Address - no delivery order.
  if (!cartAddress) {
    return { address: null, needsReorder: false, isInCustomer: false };
  }

  // Customer Addresses
  const customerAddresses: readonly Address[] = customer?.addresses ?? [];

  // By Id
  const customerAddressById = customerAddresses.find(
    ({ id }) => id === cartAddressId,
  );

  // Customer Address matches Cart Address by Id - no need to reorder.
  if (customerAddressById?.id) {
    return {
      address: customerAddressById,
      needsReorder: false,
      isInCustomer: Boolean(customerAddressById.name),
    };
  }

  // By Google Place Id / Street Data
  const customerAddressByMatchingData = customerAddresses.find(
    (customerAddress) =>
      checkIfAddressesAreTheSame(customerAddress, cartAddress),
  );

  // Address exists with a different id
  if (customerAddressByMatchingData?.id) {
    return {
      address: customerAddressByMatchingData,
      needsReorder: true,
      isInCustomer: Boolean(customerAddressByMatchingData.name),
    };
  }

  return { address: cartAddress, needsReorder: false, isInCustomer: false };
};

const checkIfAddressesAreTheSame = (
  firstAddress?: Partial<Address>,
  secondAddress?: Partial<Address>,
) => {
  const hasSameId = firstAddress?.id === secondAddress?.id;
  const hasSamePlaceId =
    firstAddress?.googlePlaceId === secondAddress?.googlePlaceId;
  const hasSameStreet = firstAddress?.street === secondAddress?.street;
  const hasSame2ndStreet =
    firstAddress?.secondaryStreet === secondAddress?.secondaryStreet;
  const hasSameZipCode = firstAddress?.zipCode === secondAddress?.zipCode;

  // Same Id means it's the same object.
  if (hasSameId) return true;

  // Same Place Id means it's the same place with a different Id.
  if (hasSamePlaceId) return true;

  // Different Id and Place Id but same place.
  if (hasSameStreet && hasSame2ndStreet && hasSameZipCode) return true;

  // Addresses are not the same.
  return false;
};

type UseOrderAddressProps = {
  readonly cart: ReturnType<typeof useCart>['cart'];
  readonly customer: ReturnType<typeof useCustomer>['customer'];
};
